import React, { createContext, useContext, useRef, useCallback } from "react";

const SoundsContext = createContext(null);

export const useSounds = () => {
  const context = useContext(SoundsContext);
  if (!context) {
    throw new Error("useSounds must be used within SoundsProvider");
  }
  return context;
};

export const SoundsProvider = ({ children }) => {
  const soundsEnabledRef = useRef(true);
  const audioCacheRef = useRef({});

  // Sound URLs (using free sound effects from reliable sources)
  const soundUrls = {
    levelUp: "https://www.soundjay.com/buttons/sounds/button-09a.mp3",
    achievementUnlock: "https://www.soundjay.com/buttons/sounds/button-35.mp3",
    challengeComplete: "https://www.soundjay.com/buttons/sounds/button-3.mp3",
    xpGain: "https://www.soundjay.com/buttons/sounds/button-1.mp3",
    streak: "https://www.soundjay.com/misc/sounds/bell-ringing-05.mp3",
    friendRequest: "https://www.soundjay.com/misc/sounds/bell-ringing-01.mp3",
  };

  // Load and cache audio
  const loadSound = useCallback((soundKey) => {
    if (audioCacheRef.current[soundKey]) {
      return audioCacheRef.current[soundKey];
    }

    const url = soundUrls[soundKey];
    if (!url) return null;

    try {
      const audio = new Audio(url);
      audioCacheRef.current[soundKey] = audio;
      return audio;
    } catch (error) {
      console.error(`Error loading sound ${soundKey}:`, error);
      return null;
    }
  }, []);

  // Play sound
  const playSound = useCallback((soundKey, options = {}) => {
    if (!soundsEnabledRef.current) return;

    const { volume = 0.5, rate = 1.0 } = options;

    const audio = loadSound(soundKey);
    if (!audio) return;

    try {
      audio.currentTime = 0;
      audio.volume = Math.min(1, Math.max(0, volume));

      // Note: playbackRate is not supported in all browsers
      if (audio.playbackRate !== undefined) {
        audio.playbackRate = rate;
      }

      audio.play().catch((error) => {
        // Auto-play prevention or other errors
        console.warn(`Could not play sound ${soundKey}:`, error);
      });
    } catch (error) {
      console.error(`Error playing sound ${soundKey}:`, error);
    }
  }, [loadSound]);

  // Specific play methods for common sounds
  const playLevelUp = useCallback(() => {
    playSound("levelUp", { volume: 0.7, rate: 1.2 });
  }, [playSound]);

  const playAchievementUnlock = useCallback(() => {
    playSound("achievementUnlock", { volume: 0.6, rate: 1.1 });
  }, [playSound]);

  const playChallengeComplete = useCallback(() => {
    playSound("challengeComplete", { volume: 0.6, rate: 1.0 });
  }, [playSound]);

  const playXPGain = useCallback(() => {
    playSound("xpGain", { volume: 0.3, rate: 1.0 });
  }, [playSound]);

  const playStreak = useCallback(() => {
    playSound("streak", { volume: 0.5, rate: 1.0 });
  }, [playSound]);

  const playFriendRequest = useCallback(() => {
    playSound("friendRequest", { volume: 0.6, rate: 1.0 });
  }, [playSound]);

  // Toggle sounds
  const toggleSounds = useCallback(() => {
    soundsEnabledRef.current = !soundsEnabledRef.current;
    return soundsEnabledRef.current;
  }, []);

  const setSoundsEnabled = useCallback((enabled) => {
    soundsEnabledRef.current = enabled;
  }, []);

  const isSoundsEnabled = useCallback(() => {
    return soundsEnabledRef.current;
  }, []);

  // Preload all sounds on mount
  React.useEffect(() => {
    Object.keys(soundUrls).forEach((soundKey) => {
      loadSound(soundKey);
    });
  }, [loadSound]);

  const value = {
    playSound,
    playLevelUp,
    playAchievementUnlock,
    playChallengeComplete,
    playXPGain,
    playStreak,
    playFriendRequest,
    toggleSounds,
    setSoundsEnabled,
    isSoundsEnabled,
  };

  return (
    <SoundsContext.Provider value={value}>
      {children}
    </SoundsContext.Provider>
  );
};

export default SoundsContext;
